// components/DataEntry/ExcelUpload.jsx — multi-sheet transaction upload
import React, { useState, useRef } from "react";
import {
  Text, Button, Badge, Spinner,
  MessageBar, MessageBarBody,
} from "@fluentui/react-components";
import { ArrowUpload24Regular, CheckmarkCircle24Regular, LockClosed24Regular } from "@fluentui/react-icons";
import * as XLSX from "xlsx";
import { useUserAccess } from "../../context/UserAccessContext";

// ─── Sheet → transaction collection mapping ───────────────────────────────────
// Each entry: { sheetName, collectionKey, requiredCol, parser }
const SHEET_MAP = [
  {
    sheetName: "Contracts",
    collectionKey: "contracts",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:       `xl-ct-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"]),
      date:     norm(row["Date"] || row["Week_Ending"]),
      entity:   str(row["Entity"]),
      title:    str(row["Contract_Title"] || row["Title"]),
      vendor:   str(row["Supplier"] || row["Vendor"]),
      value:    num(row["Value_NGN"] || row["Value"]),
      type:     str(row["Type"]).toLowerCase() || "executed",
      category: str(row["Category"]),
      status:   str(row["Status"]) || "active",
      notes:    str(row["Notes"]),
    }),
  },
  {
    sheetName: "Purchase Orders",
    collectionKey: "purchaseOrders",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:          `xl-po-${Date.now()}-${i}`,
      weekEnding:  norm(row["Week_Ending"]),
      date:        norm(row["Date"] || row["Week_Ending"]),
      entity:      str(row["Entity"]),
      poNumber:    str(row["PO_Number"] || row["PO Number"]),
      vendor:      str(row["Supplier"] || row["Vendor"]),
      description: str(row["Description"]),
      amountUSD:   num(row["Amount_USD"] || row["Amount"] || row["PO_Spend_USD"]),
      cycleTime:   num(row["Cycle_Time_Days"] || row["Cycle Time (days)"]),
      category:    str(row["Category"]),
      subcategory: str(row["Subcategory"] || row["Sub_Category"]),
    }),
  },
  {
    sheetName: "CSC Memos",
    collectionKey: "cscMemos",
    requiredCol: "Date",
    parser: (row, i) => ({
      id:         `xl-csc-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"] || row["Date"]),
      date:       norm(row["Date"]),
      entity:     str(row["Entity"]),
      title:      str(row["CSC_Memo_Title"] || row["Title"]),
      value:      num(row["Value"]),
      status:     str(row["Status"]) || "pending",
      comments:   str(row["Comments"]),
    }),
  },
  {
    sheetName: "Tender Board",
    collectionKey: "tenderBoardItems",
    requiredCol: "Date",
    parser: (row, i) => ({
      id:         `xl-tb-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"] || row["Date"]),
      date:       norm(row["Date"]),
      entity:     str(row["Entity"]),
      tbNumber:   str(row["TB_Number"] || row["TB No"]),
      title:      str(row["Proposal_Title"] || row["Title"]),
      supplier:   str(row["Supplier"]),
      amount:     num(row["Amount"]),
      type:       str(row["Proposal_Type"] || row["Type"]) || "Minor",
      status:     str(row["Status"]) || "approved",
    }),
  },
  {
    sheetName: "Suppliers",
    collectionKey: "suppliers",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:            `xl-sup-${Date.now()}-${i}`,
      weekEnding:    norm(row["Week_Ending"]),
      date:          norm(row["Date"] || row["Week_Ending"]),
      name:          str(row["Supplier_Name"] || row["Name"]),
      category:      str(row["Category"]),
      status:        str(row["Status"]) || "onboarded",
      contactPerson: str(row["Contact_Person"]),
      email:         str(row["Email"]),
    }),
  },
  {
    sheetName: "RFX",
    collectionKey: "rfxItems",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:         `xl-rfx-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"]),
      date:       norm(row["Date"] || row["Week_Ending"]),
      entity:     str(row["Entity"]),
      title:      str(row["RFX_Title"] || row["Title"]),
      type:       str(row["Type"]) || "RFQ",
      status:     str(row["Status"]) || "issued",
    }),
  },
  {
    sheetName: "Stakeholder Engagements",
    collectionKey: "stakeholderEngagements",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:          `xl-se-${Date.now()}-${i}`,
      weekEnding:  norm(row["Week_Ending"]),
      date:        norm(row["Date"] || row["Week_Ending"]),
      description: str(row["Description"]),
      stakeholder: str(row["Stakeholder"]),
      type:        str(row["Type"]) || "meeting",
    }),
  },
  {
    sheetName: "Cost Savings",
    collectionKey: "costSavings",
    requiredCol: "Week_Ending",
    parser: (row, i) => {
      const baseline    = num(row["Baseline_Cost"]    || row["Baseline Cost (Original Cost)"]);
      const negotiated  = num(row["Negotiated_Cost"]  || row["Negotiated / Revised Cost"]);
      const savingsAmt  = num(row["Savings_Amount"]   || row["Savings Amount"]);
      const savingsPct  = num(row["Savings_Pct"]      || row["Savings %"]);
      return {
        id:             `xl-cs-${Date.now()}-${i}`,
        weekEnding:     norm(row["Week_Ending"]),
        date:           norm(row["Date"] || row["Week_Ending"]),
        category:       str(row["Category"] || row["Project / Category"] || row["Project_Category"]),
        description:    str(row["Description"]),
        supplier:       str(row["Supplier"]),
        baselineCost:   baseline,
        negotiatedCost: negotiated,
        savingsAmount:  savingsAmt != null ? savingsAmt : (baseline != null && negotiated != null ? baseline - negotiated : null),
        savingsPercent: savingsPct != null ? (savingsPct > 1 ? savingsPct / 100 : savingsPct) : null,
        savingsFrequency: str(row["Frequency"] || row["One-Off or Recurring"]) || "One-Off",
        savingsType:    str(row["Savings_Type"] || row["Savings Type"]) || "Negotiation",
        notes:          str(row["Notes"]),
      };
    },
  },
  {
    sheetName: "Diesel",
    collectionKey: "dieselRecords",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:            `xl-dsl-${Date.now()}-${i}`,
      weekEnding:    norm(row["Week_Ending"]),
      openingStock:  num(row["Opening_Stock"] || row["Diesel_Opening_Stock"]),
      supply:        num(row["Supply"] || row["Diesel_Supply"]),
      consumption:   num(row["Consumption"] || row["Diesel_Consumption"]),
      closingStock:  num(row["Closing_Stock"] || row["Diesel_Closing_Stock"]),
      location:      str(row["Location"]) || "Uyo Base",
    }),
  },
  {
    sheetName: "Logistics",
    collectionKey: "logisticsRecords",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:         `xl-log-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"]),
      trips:      num(row["Trips"] || row["Uyo_Land_Logistics_trips"]),
      spend:      num(row["Spend_NGN"] || row["Uyo_Land_Logistics_Activities_Spend"]),
      route:      str(row["Route"]) || "Uyo Land",
      notes:      str(row["Notes"]),
    }),
  },
  {
    sheetName: "Projects",
    collectionKey: "projectUpdates",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:          `xl-proj-${Date.now()}-${i}`,
      weekEnding:  norm(row["Week_Ending"]),
      key:         str(row["Project_Key"] || row["Key"]),
      projectName: str(row["Project_Name"] || row["Project"]),
      progress:    num(row["Progress"]),
      notes:       str(row["Notes"]),
    }),
  },
  {
    sheetName: "Process Notes",
    collectionKey: "processNotes",
    requiredCol: "Week_Ending",
    parser: (row, i) => ({
      id:         `xl-pn-${Date.now()}-${i}`,
      weekEnding: norm(row["Week_Ending"]),
      text:       str(row["Process_Improvement"] || row["Text"]),
      highlights: str(row["Highlights"]),
    }),
  },
];

// Helpers
function norm(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Excel serial number — UTC-based arithmetic, no local shift
  if (/^\d+$/.test(s)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Number(s) * 86400000);
    if (!isNaN(d)) return d.toISOString().split("T")[0];
  }
  // All other date strings: parse then extract LOCAL date parts to avoid UTC shift
  const parsed = new Date(s);
  if (!isNaN(parsed)) {
    const y  = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const dy = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  }
  return s;
}
function str(v)  { return v != null ? String(v).trim() : ""; }
function num(v)  { if (v == null || v === "") return null; const n = parseFloat(String(v).replace(/,/g, "").replace(/[₦$£€\s]/g, "")); return isNaN(n) ? null : n; }

function parseSheets(wb) {
  const result = {};
  let totalRows = 0;

  for (const { sheetName, collectionKey, requiredCol, parser } of SHEET_MAP) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" });
    const valid = rows.filter((r) => {
      const v = r[requiredCol];
      return v && /\d/.test(String(v));
    });
    if (valid.length === 0) continue;
    result[collectionKey] = valid.map(parser);
    totalRows += valid.length;
  }

  return { transactions: result, totalRows };
}

// ─── Template download ────────────────────────────────────────────────────────
function downloadTemplate() {
  const wb = XLSX.utils.book_new();

  const makeSheet = (headers, typeHints, sampleRows) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, typeHints, ...sampleRows]);
    ws["!cols"] = headers.map((h, i) => ({ wch: i === 0 ? 14 : h.length + 4 }));
    return ws;
  };

  const weekFmt = "YYYY-MM-DD";

  // Contracts
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Entity","Contract_Title","Supplier","Value_NGN","Type","Category","Status","Notes"],
    [weekFmt,weekFmt,"text","text","text","number","executed / awarded","text","active / pending-execution","text"],
    [["2026-03-13","2026-03-10","Eroton E&P","Provision of Well Engineering Design","Petraserv Nigeria Ltd",12500000,"executed","Well Services","active",""],
     ["2026-03-13","2026-03-11","Eroton E&P","Medical Services Framework","Flying Doctors Nigeria",18000000,"awarded","Health & Safety","pending-execution","Board approval pending"]]
  ), "Contracts");

  // Purchase Orders
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Entity","PO_Number","Supplier","Description","Amount_USD","Cycle_Time_Days","Category","Subcategory"],
    [weekFmt,weekFmt,"text","text","text","text","number","number","text","text"],
    [["2026-03-13","2026-03-10","LEKOIL","PO-2026-042","Jastis Tech Ltd","Laptop Procurement – 5 Units",1800,2,"IT Equipment","Hardware"],
     ["2026-03-13","2026-03-10","LEKOIL","PO-2026-043","Flying Doctors Nigeria","Medical Emergency Response Retainer",15000,3,"Health & Safety","Medical"]]
  ), "Purchase Orders");

  // CSC Memos
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Entity","CSC_Memo_Title","Value","Status","Comments"],
    [weekFmt,weekFmt,"text","text","number","approved / pending","text"],
    [["2026-03-13","2026-03-10","Eroton E&P","CSC for Wellhead Control Panel Servicing",6500000,"pending","Awaiting GM approval"],
     ["2026-03-13","2026-03-11","Eroton E&P","CSC for HSE Quarterly Request",1800000,"pending","Under review"]]
  ), "CSC Memos");

  // Tender Board
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Entity","TB_Number","Proposal_Title","Supplier","Amount","Proposal_Type","Status"],
    [weekFmt,weekFmt,"text","text","text","text","number","Major / Minor","approved / pending"],
    [["2026-03-13","2026-03-10","Eroton E&P","MTB-2026-007","Medical Services – Flying Doctors Nigeria","Flying Doctors Nigeria",18000000,"Major","approved"],
     ["2026-03-13","2026-03-10","Eroton E&P","MTrB-2026-008","IT Infrastructure Support","Jastis Tech Ltd",838318,"Minor","approved"]]
  ), "Tender Board");

  // Suppliers
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Supplier_Name","Category","Status","Contact_Person","Email"],
    [weekFmt,weekFmt,"text","text","onboarded","text","email"],
    [["2026-03-13","2026-03-10","Jastis Tech Ltd","IT Services","onboarded","Ahmed Bello","abello@jastis.com"]]
  ), "Suppliers");

  // RFX
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Entity","RFX_Title","Type","Status"],
    [weekFmt,weekFmt,"text","text","RFP / RFQ / EOI","issued / closed"],
    [["2026-03-13","2026-03-10","Eroton E&P","RFP for Medical Services at Otakikpo","RFP","issued"],
     ["2026-03-13","2026-03-11","Eroton E&P","RFQ for Office Furniture Procurement","RFQ","issued"]]
  ), "RFX");

  // Stakeholder Engagements
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Description","Stakeholder","Type"],
    [weekFmt,weekFmt,"text","text","meeting / negotiation / community / regulatory / presentation"],
    [["2026-03-13","2026-03-10","Meeting with NUIMS re: local content","NUIMS","meeting"],
     ["2026-03-13","2026-03-11","Vendor engagement session","AfricaWorks","meeting"]]
  ), "Stakeholder Engagements");

  // Cost Savings
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Date","Category","Description","Supplier","Baseline_Cost","Negotiated_Cost","Savings_Amount","Savings_Pct","Frequency","Savings_Type","Notes"],
    [weekFmt,weekFmt,"text","text","text","number","number","number","decimal","text","text","text"],
    [["2026-03-13","2026-03-10","IT Equipment","Server procurement re-negotiation","Jastis Tech Ltd",5000000,3800000,1200000,0.24,"One-Off","Negotiation","Vendor accepted revised scope"],
     ["2026-03-13","2026-03-11","Logistics","Annual haulage contract consolidation","EagleTrans Logistics",8000000,6200000,1800000,0.225,"Recurring","Contract Consolidation","Combined 3 routes into 1 contract"]]
  ), "Cost Savings");

  // Diesel
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Opening_Stock","Supply","Consumption","Closing_Stock","Location"],
    [weekFmt,"litres","litres","litres","litres","text"],
    [["2026-03-13",8421,33000,11850,29571,"Uyo Base"],
     ["2026-03-06",23851,0,15430,8421,"Uyo Base"]]
  ), "Diesel");

  // Logistics
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Route","Trips","Spend_NGN","Notes"],
    [weekFmt,"text","number","number","text"],
    [["2026-03-13","Uyo Land",13,3920000,""],
     ["2026-03-06","Uyo Land",7,2030000,""]]
  ), "Logistics");

  // Projects
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Project_Key","Project_Name","Progress","Notes"],
    [weekFmt,"uyoPortacabins / uyoBaseProgress / cateringProgress","text","decimal 0-1","text"],
    [["2026-03-13","uyoBaseProgress","Uyo Base Progress",1,"Complete"],
     ["2026-01-16","uyoPortacabins","Uyo Portacabins",0.2,"Foundations done"]]
  ), "Projects");

  // Process Notes
  XLSX.utils.book_append_sheet(wb, makeSheet(
    ["Week_Ending","Process_Improvement","Highlights"],
    [weekFmt,"text","text"],
    [["2026-03-13","Category-based structure fully implemented. CRAF now mandatory.","Tender Board handbook published."]]
  ), "Process Notes");

  XLSX.writeFile(wb, "LekOil_SCM_Upload_Template.xlsx");
}

// ─── Summary counts for preview ───────────────────────────────────────────────
function summarize(transactions) {
  const lines = [];
  const labels = {
    contracts: "Contracts", purchaseOrders: "Purchase Orders", cscMemos: "CSC Memos",
    tenderBoardItems: "TB Items", suppliers: "Suppliers", rfxItems: "RFX",
    stakeholderEngagements: "Stakeholder Engagements", supplierPerformanceReviews: "Supplier Reviews",
    dieselRecords: "Diesel Records", logisticsRecords: "Logistics Records",
    projectUpdates: "Project Updates", processNotes: "Process Notes",
  };
  for (const [key, label] of Object.entries(labels)) {
    const arr = transactions[key];
    if (arr?.length) lines.push({ key, label, count: arr.length });
  }
  return lines;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ExcelUpload({ onBulkUpload }) {
  const { isAdmin } = useUserAccess();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { transactions, totalRows, summary }
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const processFile = (f) => {
    if (!f) return;
    setFile(f); setError(null); setResults(null); setPreview(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary", cellDates: true });
        const { transactions, totalRows } = parseSheets(wb);
        if (totalRows === 0) {
          setError("No valid data rows found. Ensure sheet names match the template (e.g. 'Contracts', 'Purchase Orders', 'Diesel') and each row has a valid date in Week_Ending.");
          setFile(null);
          if (fileRef.current) fileRef.current.value = "";
        } else {
          setPreview({ transactions, totalRows, summary: summarize(transactions) });
        }
      } catch (err) {
        setError("Could not parse file: " + err.message);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    reader.readAsBinaryString(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith(".xlsx") || f.name.endsWith(".xls"))) processFile(f);
    else setError("Please upload an .xlsx or .xls file.");
  };

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true); setError(null);
    try {
      const res = await onBulkUpload(preview.transactions);
      setResults({ total: preview.totalRows, summary: preview.summary, created: res?.created, updated: res?.updated });
      setPreview(null); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  if (!isAdmin) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px", textAlign: "center" }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: "#FDE7E9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
        <LockClosed24Regular style={{ fontSize: 26, color: "#C4262E" }} />
      </div>
      <Text size={500} weight="semibold" style={{ color: "#001A3D", display: "block", marginBottom: 8 }}>Admin Access Required</Text>
      <Text style={{ color: "#7E8CA0", fontSize: 13 }}>Excel upload is restricted to administrators only.</Text>
    </div>
  );

  return (
    <div style={{ maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <Text weight="bold" size={500} style={{ color: "#201F1E", display: "block", marginBottom: 4 }}>Transaction Data Upload</Text>
          <Text style={{ color: "#605E5C", fontSize: 13 }}>
            Upload an Excel workbook with multiple sheets — one per transaction type. Dashboard metrics are automatically computed from the uploaded records.
          </Text>
        </div>
        <button
          onClick={downloadTemplate}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", background: "linear-gradient(135deg,#0052CC,#003DA6)", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", boxShadow: "0 4px 14px rgba(0,82,204,0.28)" }}
        >
          ⬇ Download Template
        </button>
      </div>

      {/* Sheet guide */}
      <div style={{ background: "#EBF3FF", border: "1px solid #C7E0FF", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
        <Text weight="semibold" style={{ color: "#0052CC", fontSize: 13, display: "block", marginBottom: 8 }}>
          Expected Sheets ({SHEET_MAP.length})
        </Text>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SHEET_MAP.map(({ sheetName, collectionKey }) => (
            <Badge key={collectionKey} appearance="tint" color="informative" style={{ fontSize: 10 }}>{sheetName}</Badge>
          ))}
        </div>
        <Text style={{ fontSize: 11, color: "#3A6CA8", marginTop: 8, display: "block" }}>
          💡 You don't need every sheet — only include the sheets with new data. Metrics are summed/counted from each transaction type.
        </Text>
      </div>

      {/* Drop zone */}
      {!preview && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current.click()}
          style={{ border: `2px dashed ${dragOver ? "#0052CC" : "#C8C6C4"}`, borderRadius: 16, padding: 48, textAlign: "center", cursor: "pointer", background: dragOver ? "#EBF3FF" : "#FAFAFA", transition: "all 0.2s", marginBottom: 20 }}
        >
          <ArrowUpload24Regular style={{ fontSize: 40, color: "#0052CC", display: "block", margin: "0 auto 12px" }} />
          <Text weight="semibold" style={{ fontSize: 16, color: "#201F1E", display: "block", marginBottom: 4 }}>Drop Excel workbook here</Text>
          <Text style={{ color: "#605E5C", fontSize: 13 }}>or click to browse · .xlsx, .xls</Text>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={(e) => processFile(e.target.files[0])} />
        </div>
      )}

      {error && <MessageBar intent="error" style={{ marginBottom: 14, borderRadius: 8 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}

      {/* Success result */}
      {results && (
        <MessageBar intent="success" style={{ marginBottom: 14, borderRadius: 8 }}>
          <MessageBarBody>
            <CheckmarkCircle24Regular style={{ verticalAlign: "middle", marginRight: 6 }} />
            <strong>{results.total} transaction records</strong> processed —{" "}
            {results.created > 0 && <><strong>{results.created} new</strong>{results.updated > 0 ? ", " : ". "}</>}
            {results.updated > 0 && <><strong>{results.updated} overwritten</strong>. </>}
            ({results.summary.map((s) => `${s.count} ${s.label}`).join(", ")}).
            Dashboard metrics have been recomputed.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Preview */}
      {preview && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 24, border: "1px solid #EDEBE9", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <Text weight="semibold" size={400} style={{ color: "#201F1E", display: "block" }}>Preview — {preview.totalRows} Records Detected</Text>
              <Text style={{ fontSize: 12, color: "#605E5C" }}>{file?.name}</Text>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button appearance="outline" onClick={() => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; }} disabled={uploading}>Remove</Button>
              <Button
                appearance="primary"
                onClick={handleUpload}
                disabled={uploading}
                icon={uploading ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
                style={{ background: "#0052CC" }}
              >
                {uploading ? "Importing..." : `Import ${preview.totalRows} Records`}
              </Button>
            </div>
          </div>

          {uploading && <div style={{ height: 4, background: "#EBF3FF", borderRadius: 2, overflow: "hidden", marginBottom: 16 }}><div style={{ height: "100%", background: "#0052CC", borderRadius: 2, animation: "progress 1.5s ease-in-out infinite" }} /></div>}

          {/* Summary grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
            {preview.summary.map(({ key, label, count }) => (
              <div key={key} style={{ background: "#F8F9FA", borderRadius: 10, padding: "12px 14px", border: "1px solid #EAECEF" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#0052CC", lineHeight: 1 }}>{count}</div>
                <div style={{ fontSize: 11, color: "#605E5C", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          <Text style={{ fontSize: 11, color: "#8A8886", marginTop: 12, display: "block" }}>
            💡 Summary metrics on the Dashboard will be automatically recomputed after import.
          </Text>
        </div>
      )}
      <style>{`@keyframes progress { 0%{width:0%} 50%{width:60%} 100%{width:100%} }`}</style>
    </div>
  );
}
